import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, useToast } from '../../components/ui';
import type { BadgeTone } from '../../components/ui';
import { useUser } from '../../context/UserContext';
import { supabase } from '../../lib/supabase';
import { playChime } from '../../lib/chime';
import {
  browserOf,
  describeDevice,
  disablePush,
  enablePush,
  getPushSubscription,
  needsHomeScreenInstall,
  platformOf,
  pushDenied,
  pushSupported,
} from '../../lib/push';

interface DeviceRow {
  id: string;
  endpoint: string;
  user_agent: string | null;
  created_at: string;
}

type PushState = 'on' | 'off' | 'blocked' | 'install' | 'unsupported';

const PUSH_BADGE: Record<PushState, { tone: BadgeTone; label: string }> = {
  on: { tone: 'up', label: 'On' },
  off: { tone: 'idle', label: 'Off' },
  blocked: { tone: 'down', label: 'Blocked' },
  install: { tone: 'warn', label: 'Needs the app' },
  unsupported: { tone: 'idle', label: 'Not supported' },
};

const muted: React.CSSProperties = { margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 };
const strong: React.CSSProperties = { color: 'var(--text-body)', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'] };

/** OS-level switches that silence a browser's notifications, for this device. */
function osTips(ua: string): React.ReactNode[] {
  const browser = browserOf(ua);
  switch (platformOf(ua)) {
    case 'mac':
      return [
        <>
          <span style={strong}>System Settings → Notifications → {browser}</span>: turn on Allow notifications and Play
          sound for notification.
        </>,
        <>
          Focus or Do Not Disturb silences everything. Check the moon in Control Center.
        </>,
      ];
    case 'windows':
      return [
        <>
          <span style={strong}>Settings → System → Notifications → {browser}</span>: on, with “Play a sound when a
          notification arrives”.
        </>,
        <>Do not disturb silences everything. Check the bell in the taskbar.</>,
      ];
    case 'iphone':
      return [
        <>
          Push only works in the Home Screen app: in Safari tap <span style={strong}>Share → Add to Home Screen</span>,
          open Cockpit from that icon and turn push on here.
        </>,
        <>
          <span style={strong}>Settings → Notifications → Cockpit</span>: Allow Notifications and Sounds on. Focus modes
          can silence it.
        </>,
      ];
    case 'android':
      return [
        <>
          <span style={strong}>Settings → Apps → {browser} → Notifications</span>: allowed, with sound on.
        </>,
      ];
    default:
      return [<>Allow notifications for {browser} in your system’s notification settings, with sound on.</>];
  }
}

/** How to unblock this site in the browser after a "Block" on the permission prompt. */
function unblockHint(ua: string): string {
  if (browserOf(ua) === 'Safari') {
    return 'Notifications are blocked for Cockpit. In Safari → Settings → Websites → Notifications, set this site to Allow, then reload.';
  }
  return 'Notifications are blocked for Cockpit. Click the icon left of the address bar → Notifications → Allow, then reload.';
}

function Step({
  n,
  title,
  badge,
  action,
  children,
}: {
  n: number;
  title: string;
  badge?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', gap: 'var(--space-3)', padding: 'var(--space-4) 0', borderTop: n === 1 ? 'none' : '1px solid var(--border-hairline)' }}>
      <span
        style={{
          flexShrink: 0,
          width: '22px',
          height: '22px',
          borderRadius: 'var(--radius-pill)',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border-hairline)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--text-xs)',
          color: 'var(--text-muted)',
        }}
      >
        {n}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ ...strong, fontSize: 'var(--text-sm)' }}>{title}</span>
            {badge}
          </div>
          {action}
        </div>
        <div style={{ marginTop: '4px' }}>{children}</div>
      </div>
    </div>
  );
}

/**
 * Settings → Notifications: get dings working on this device (push, tab
 * sound, a test alert, OS tips) and see every device that gets pushes.
 */
export default function NotificationSetupCard() {
  const { user } = useUser();
  const toast = useToast();
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;

  const [pushState, setPushState] = useState<PushState>('off');
  const [thisEndpoint, setThisEndpoint] = useState<string | null>(null);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [testSentAt, setTestSentAt] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    const sub = await getPushSubscription().catch(() => null);
    setThisEndpoint(sub?.endpoint ?? null);
    if (!pushSupported()) setPushState('unsupported');
    else if (needsHomeScreenInstall()) setPushState('install');
    else if (pushDenied()) setPushState('blocked');
    else setPushState(sub ? 'on' : 'off');

    const { data, error } = await supabase
      .from('push_subscriptions')
      .select('id, endpoint, user_agent, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });
    if (error) console.error('Failed to load push devices:', error);
    else setDevices(data ?? []);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const togglePush = async () => {
    if (!user || busy) return;
    setBusy(true);
    try {
      if (pushState === 'on') await disablePush();
      else await enablePush(user.id);
    } catch (err) {
      console.error('Push toggle failed:', err);
      toast.error('Couldn’t change push for this device', err instanceof Error ? err.message : undefined);
    } finally {
      await refresh();
      setBusy(false);
    }
  };

  const removeDevice = async (device: DeviceRow) => {
    try {
      if (device.endpoint === thisEndpoint) {
        await disablePush();
      } else {
        const { error } = await supabase.from('push_subscriptions').delete().eq('id', device.id);
        if (error) throw error;
      }
    } catch (err) {
      console.error('Failed to remove push device:', err);
      toast.error('Couldn’t remove that device');
    }
    await refresh();
  };

  const sendTest = async () => {
    if (!user) return;
    const { error } = await supabase.from('notifications').insert({
      user_id: user.id,
      type: 'test',
      title: 'Test alert',
      body: 'If you heard a ding and saw this, notifications work here.',
      link: '/settings/notifications',
    });
    if (error) {
      console.error('Failed to send test alert:', error);
      toast.error('Couldn’t send a test alert');
      return;
    }
    setTestSentAt(Date.now());
  };

  const pushHint: Record<PushState, React.ReactNode> = {
    on: 'This device gets a banner and a sound for every alert set to Ding, even with Cockpit closed.',
    off: 'Turn this on for a banner and a sound even when Cockpit is closed. Do it on each computer and phone you use.',
    blocked: unblockHint(ua),
    install: osTips(ua)[0],
    unsupported: 'This browser can’t receive push. Chrome, Edge, Firefox or Safari can.',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <Card eyebrow="Setup" title="Get dings on this device">
        <Step
          n={1}
          title="Push notifications"
          badge={<Badge tone={PUSH_BADGE[pushState].tone} dot>{PUSH_BADGE[pushState].label}</Badge>}
          action={
            pushState === 'on' || pushState === 'off' ? (
              <Button size="sm" variant={pushState === 'on' ? 'ghost' : 'primary'} onClick={togglePush} disabled={busy}>
                {busy ? 'Working…' : pushState === 'on' ? 'Turn off' : 'Turn on'}
              </Button>
            ) : null
          }
        >
          <p style={muted}>{pushHint[pushState]}</p>
        </Step>

        <Step
          n={2}
          title="Sound while Cockpit is open"
          action={
            <Button size="sm" variant="secondary" onClick={playChime}>
              Play the ding
            </Button>
          }
        >
          <p style={muted}>
            An open Cockpit tab chimes when an alert lands. Browsers only let a tab make sound after you click in it, so
            after a reload, click anywhere in Cockpit once. Check your volume and that the tab isn’t muted.
          </p>
        </Step>

        <Step
          n={3}
          title="Send yourself a test"
          action={
            <Button size="sm" variant="secondary" onClick={sendTest}>
              Send test alert
            </Button>
          }
        >
          <p style={muted}>
            {testSentAt
              ? 'Sent. Within a few seconds you should hear the ding, see it in the bell, and get a banner on every device listed below.'
              : 'Puts a test alert in your bell and pushes it to every device below.'}
          </p>
        </Step>

        <Step n={4} title="Still quiet? Check the device’s settings">
          <ul style={{ ...muted, margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {osTips(ua)
              .slice(pushState === 'install' ? 1 : 0)
              .map((tip, i) => (
                <li key={i}>{tip}</li>
              ))}
          </ul>
        </Step>
      </Card>

      <Card eyebrow="Devices" title={devices.length === 1 ? '1 device gets pushes' : `${devices.length} devices get pushes`}>
        {devices.length === 0 ? (
          <p style={muted}>None yet. Turn on push above on each computer and phone you want dinged.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {devices.map((device, i) => (
              <div
                key={device.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3) 0',
                  borderTop: i === 0 ? 'none' : '1px solid var(--border-hairline)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <span style={{ ...strong, fontSize: 'var(--text-sm)' }}>{describeDevice(device.user_agent)}</span>
                    {device.endpoint === thisEndpoint ? <Badge tone="accent">This device</Badge> : null}
                  </div>
                  <p style={{ ...muted, fontSize: 'var(--text-xs)' }}>
                    Added {new Date(device.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeDevice(device)}>
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
